import { IconCheck, IconChevronRight } from '@tabler/icons-react';
import { motion, useReducedMotion } from 'motion/react';
import { useLayoutEffect, useRef, useState } from 'react';
import type { ComponentPropsWithRef, KeyboardEvent, ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { PRESS } from '../../interaction';
import { Avatar } from './Avatar';

export type ListSize = 'sm' | 'md' | 'lg';

export interface ListItemData {
  id: string;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Rendered as a leading avatar, ahead of `icon`. */
  avatar?: { src?: string; name: string };
  icon?: ReactNode;
  /** Right-hand slot: a badge, a button, a timestamp. */
  trailing?: ReactNode;
  disabled?: boolean;
  onPress?: () => void;
}

export interface ListProps extends Omit<ComponentPropsWithRef<'ul'>, 'children'> {
  items: ListItemData[];
  /** Accessible name for the list. */
  label?: string;
  size?: ListSize;
  /** Rules between rows. The last row never gets one. */
  divided?: boolean;
  /** Single-select mode: the list becomes a listbox of options. */
  selectable?: boolean;
  value?: string | null;
  onValueChange?: (id: string) => void;
  /** Called for any pressed row when the list is not `selectable`. */
  onItemPress?: (item: ListItemData) => void;
  /** Shows a trailing chevron on pressable rows. */
  showChevron?: boolean;
  emptyLabel?: ReactNode;
}

const SIZES: Record<ListSize, string> = {
  sm: 'gap-2 px-3 py-2',
  md: 'gap-3 px-3 py-2.5',
  lg: 'gap-3 px-4 py-3.5',
};

const TITLE_SIZE: Record<ListSize, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-md',
};

const AVATAR_SIZE = { sm: 'sm', md: 'sm', lg: 'md' } as const;

export function List({
  items,
  label,
  size = 'md',
  divided = false,
  selectable = false,
  value = null,
  onValueChange,
  onItemPress,
  showChevron = false,
  emptyLabel = 'Nothing to show.',
  className,
  onKeyDown,
  onMouseLeave,
  ...rest
}: ListProps) {
  const reduceMotion = useReducedMotion();
  const listRef = useRef<HTMLUListElement>(null);
  const rowRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [rect, setRect] = useState<{ top: number; height: number } | null>(null);

  // The rollover band is one element that moves, not a background painted on
  // every row — so it can travel between rows instead of blinking.
  useLayoutEffect(() => {
    if (activeIndex === null) {
      setRect(null);
      return;
    }
    const row = rowRefs.current[activeIndex];
    const list = listRef.current;
    if (!row || !list) {
      setRect(null);
      return;
    }
    const measure = () => setRect({ top: row.offsetTop, height: row.offsetHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    observer.observe(list);
    return () => observer.disconnect();
  }, [activeIndex]);

  function focusRow(index: number) {
    rowRefs.current[index]?.focus();
    setActiveIndex(index);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLUListElement>) {
    onKeyDown?.(event);
    if (!selectable || event.defaultPrevented) return;

    const enabled = items.map((item, index) => (item.disabled ? -1 : index)).filter((i) => i >= 0);
    if (enabled.length === 0) return;

    const current = activeIndex === null ? -1 : enabled.indexOf(activeIndex);
    let next = -1;

    if (event.key === 'ArrowDown') next = enabled[Math.min(current + 1, enabled.length - 1)] ?? -1;
    else if (event.key === 'ArrowUp') next = enabled[Math.max(current - 1, 0)] ?? -1;
    else if (event.key === 'Home') next = enabled[0] ?? -1;
    else if (event.key === 'End') next = enabled[enabled.length - 1] ?? -1;
    else if (event.key === ' ' || event.key === 'Enter') {
      if (activeIndex !== null && !items[activeIndex]?.disabled) {
        const id = items[activeIndex]?.id;
        if (id !== undefined) onValueChange?.(id);
      }
      event.preventDefault();
      return;
    } else {
      return;
    }

    if (next >= 0) {
      event.preventDefault();
      focusRow(next);
    }
  }

  // A listbox keeps exactly one option in the tab order, and a disabled row is
  // never it: a row that cannot be activated must not be the only thing a
  // keyboard can land on, and it is not reported as selected either. When no row
  // can take the stop, the listbox itself does — otherwise a list of disabled
  // rows would leave the tab order entirely.
  const firstEnabled = items.findIndex((item) => !item.disabled);
  const selectedIndex = selectable
    ? items.findIndex((item) => item.id === value && !item.disabled)
    : -1;
  const tabbableIndex = selectable ? (selectedIndex >= 0 ? selectedIndex : firstEnabled) : -1;
  const containerTabbable = selectable && tabbableIndex < 0;

  if (items.length === 0) {
    return (
      <ul
        {...rest}
        aria-label={label}
        className={cn('list-none rounded-xs border border-line-subtle bg-surface p-4', className)}
      >
        <li className="text-sm text-muted">{emptyLabel}</li>
      </ul>
    );
  }

  return (
    <ul
      {...rest}
      ref={listRef}
      role={selectable ? 'listbox' : 'list'}
      // A listbox must carry an accessible name; an unnamed one is announced as
      // a bare "list box" with no idea what it lists.
      aria-label={selectable ? (label ?? 'Options') : label}
      tabIndex={containerTabbable ? 0 : undefined}
      onKeyDown={handleKeyDown}
      onMouseLeave={(event) => {
        onMouseLeave?.(event);
        setActiveIndex(null);
      }}
      className={cn(
        // No `overflow-hidden`: the row focus ring is drawn 2px outside the row
        // and would be clipped. The rollover band is `inset-x-0` from `top-0`, so
        // it stays inside the border either way.
        'relative list-none rounded-xs border border-line-subtle bg-surface p-0',
        containerTabbable &&
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        className,
      )}
    >
      <motion.div
        aria-hidden="true"
        initial={false}
        animate={{ y: rect?.top ?? 0, height: rect?.height ?? 0, opacity: rect ? 1 : 0 }}
        transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 520, damping: 44 }}
        className="pointer-events-none absolute inset-x-0 top-0 border-l-2 border-primary bg-surface-muted"
      />

      {items.map((item, index) => {
        const isSelected = selectable && item.id === value && !item.disabled;
        const pressable = Boolean(item.onPress || onItemPress) && !selectable;
        const isLast = index === items.length - 1;

        const content = (
          <>
            {item.avatar ? (
              <Avatar
                src={item.avatar.src}
                name={item.avatar.name}
                size={AVATAR_SIZE[size]}
                className="shrink-0"
              />
            ) : item.icon ? (
              <span
                aria-hidden="true"
                className="grid size-5 shrink-0 place-items-center text-muted"
              >
                {item.icon}
              </span>
            ) : null}

            <span className="min-w-0 flex-1">
              <span className={cn('block truncate font-medium text-heading', TITLE_SIZE[size])}>
                {item.title}
              </span>
              {item.subtitle ? (
                <span className="mt-0.5 block truncate text-xs text-muted">{item.subtitle}</span>
              ) : null}
            </span>

            {item.trailing ? <span className="shrink-0 text-muted">{item.trailing}</span> : null}

            {selectable ? (
              <span
                aria-hidden="true"
                className={cn(
                  'grid size-4 shrink-0 place-items-center text-primary',
                  !isSelected && 'opacity-0',
                )}
              >
                <IconCheck className="size-4" stroke={2.5} />
              </span>
            ) : null}

            {!selectable && pressable && showChevron ? (
              <IconChevronRight aria-hidden="true" className="size-4 shrink-0 text-muted" />
            ) : null}
          </>
        );

        const rowClasses = cn(
          'flex w-full min-w-0 items-center rounded-none text-left',
          SIZES[size],
          'transition-colors duration-[var(--dur-fast)]',
          divided && !isLast && 'border-b border-line-subtle',
          item.disabled && (selectable || pressable) && 'opacity-50',
          // The shared press treatment — but only for a row that can actually be
          // pressed: a display-only row must not move under the pointer.
          !item.disabled && (selectable || pressable) && PRESS,
        );

        return (
          <li
            key={item.id}
            ref={(node) => {
              rowRefs.current[index] = node;
            }}
            role={selectable ? 'option' : undefined}
            aria-selected={selectable ? isSelected : undefined}
            aria-disabled={item.disabled ? true : undefined}
            tabIndex={selectable ? (index === tabbableIndex ? 0 : -1) : undefined}
            onMouseEnter={() => setActiveIndex(index)}
            onFocus={() => setActiveIndex(index)}
            onBlur={() => setActiveIndex(null)}
            className="relative z-10 min-w-0"
          >
            {selectable ? (
              <div
                onClick={
                  item.disabled
                    ? undefined
                    : () => {
                        onValueChange?.(item.id);
                      }
                }
                className={cn(rowClasses, !item.disabled && 'cursor-pointer')}
              >
                {content}
              </div>
            ) : pressable ? (
              <button
                type="button"
                disabled={item.disabled}
                onClick={() => {
                  item.onPress?.();
                  onItemPress?.(item);
                }}
                className={cn(
                  rowClasses,
                  'dhd-target relative cursor-pointer',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                  'disabled:pointer-events-none',
                )}
              >
                {content}
              </button>
            ) : (
              <div className={rowClasses}>{content}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
